import { FormEvent } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../../firebase";
import { api } from "../../../../services/api";
import { Account, TransactionCategory, TransactionCategoryGroup, Transaction, TransactionCategoryDetail, Currency, TransactionCustomField } from "../../../../types";
import { invalidateTransactionsCache } from "../../hooks/useTransactions";
import { deriveNoteAndAttachmentFromCustomFields } from "../../../../utils/transactionCustomFieldsDerivation";

type TransactionType = "expense" | "income" | "transfer";
type FocusedField = "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;

interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: Record<string, string | { url: string; name: string; type: string }>;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: Record<string, string | { url: string; name: string; type: string }>;
  customFieldPendingFiles?: Record<string, File>;
}

interface UseMobileTransactionSubmissionProps {
  workspaceId: string;
  workspaceSlug: string | undefined;
  transactionType: TransactionType;
  isEditMode: boolean;
  transactionId: string | undefined;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  transferFormData: TransferFormData;
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  transactions: Transaction[];
  newlyAddedDetailOptions: Record<string, string[]>;
  setNewlyAddedDetailOptions: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  focusedField: FocusedField;
  handleFieldFocus: (field: FocusedField) => void;
  combineDateAndTime: (date: string, time: string) => string;
  clearSavedState: () => void;
  routeStorageKey: string;
  isNavigatingBackRef: React.MutableRefObject<boolean>;
  setIsSubmitting: (submitting: boolean) => void;
  setUploadingAttachment: (uploading: boolean) => void;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  defaultCurrencyRateManuallyEdited: boolean;
  transactionCustomFields: TransactionCustomField[];
  returnUrl?: string | null; // Optional return URL to navigate back to (e.g., from CategoryGroupDetail)
  allowExpenseFee?: boolean;
  allowTransferFee?: boolean;
}

/**
 * Hook for managing transaction submission logic
 */
export const useMobileTransactionSubmission = (props: UseMobileTransactionSubmissionProps) => {
  const {
    workspaceId,
    workspaceSlug,
    transactionType,
    isEditMode,
    transactionId,
    formData,
    setFormData,
    transferFormData,
    accounts,
    currencies,
    defaultCurrency,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions,
    newlyAddedDetailOptions,
    setNewlyAddedDetailOptions,
    focusedField,
    handleFieldFocus,
    combineDateAndTime,
    clearSavedState,
    routeStorageKey,
    isNavigatingBackRef,
    setIsSubmitting,
    setUploadingAttachment,
    navigate,
    defaultCurrencyRateManuallyEdited,
    transactionCustomFields = [],
  } = props;

  // Validate form and focus first empty required field
  const validateAndFocusFirstEmpty = (): boolean => {
    if (transactionType === "transfer") {
      // Transfer validation
      if (!transferFormData.date || transferFormData.date.trim() === "") {
        handleFieldFocus("date");
        return false;
      }
      // Allow 0 as valid amount - check if amount exists and is not empty, and is valid (>= 0)
      // Explicitly allow "0" as valid
      const trimmedTransferAmount = transferFormData.amount ? String(transferFormData.amount).trim() : "";
      if (!transferFormData.amount || trimmedTransferAmount === "") {
        handleFieldFocus("amount");
        return false;
      }
      const transferAmountValue = parseFloat(trimmedTransferAmount);
      if (isNaN(transferAmountValue) || transferAmountValue < 0) {
        handleFieldFocus("amount");
        return false;
      }
      if (!transferFormData.fromAccountId || transferFormData.fromAccountId.trim() === "") {
        handleFieldFocus("fromAccount");
        return false;
      }
      if (!transferFormData.toAccountId || transferFormData.toAccountId.trim() === "") {
        handleFieldFocus("toAccount");
        return false;
      }
      // Check exchange rate if currencies differ
      const fromAccount = accounts.find(acc => acc.id === transferFormData.fromAccountId);
      const toAccount = accounts.find(acc => acc.id === transferFormData.toAccountId);
      if (fromAccount && toAccount && fromAccount.currency !== toAccount.currency) {
        if (!transferFormData.transferRate || transferFormData.transferRate.trim() === "" || parseFloat(transferFormData.transferRate) <= 0) {
          // Focus on rate field - find by label
          setTimeout(() => {
            const rateLabel = Array.from(document.querySelectorAll('label')).find(label => label.textContent === 'Exchange Rate');
            if (rateLabel) {
              const rateField = rateLabel.closest('.MuiFormControl')?.querySelector('input') as HTMLInputElement;
              if (rateField) {
                rateField.focus();
                rateField.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }
          }, 100);
          return false;
        }
      }
      // Check fee fields if fee is shown
      if (transferFormData.feeAmount !== undefined) {
        const feeAmountValue = transferFormData.feeAmount.trim() === "" ? 0 : parseFloat(transferFormData.feeAmount);
        // If fee amount is provided (not empty), validate it (allow 0)
        if (transferFormData.feeAmount.trim() !== "") {
          if (isNaN(feeAmountValue) || feeAmountValue < 0) {
            handleFieldFocus("feeAmount");
            return false;
          }
          // If fee amount is > 0, require fee account
          if (feeAmountValue > 0 && (!transferFormData.feeAccountId || transferFormData.feeAccountId.trim() === "")) {
            handleFieldFocus("feeAccount");
            return false;
          }
        }
      }
    } else {
      // Expense/Income validation
      if (!formData.date || formData.date.trim() === "") {
        handleFieldFocus("date");
        return false;
      }
      // Allow 0 as valid amount - check if amount exists and is not empty, and is valid (>= 0)
      // Explicitly allow "0" as valid
      const trimmedAmount = formData.amount ? String(formData.amount).trim() : "";
      if (!formData.amount || trimmedAmount === "") {
        handleFieldFocus("amount");
        return false;
      }
      const amountValue = parseFloat(trimmedAmount);
      if (isNaN(amountValue) || amountValue < 0) {
        handleFieldFocus("amount");
        return false;
      }
      if (!formData.category || formData.category.trim() === "") {
        handleFieldFocus("category");
        return false;
      }
      // Account will be auto-selected on submit if not selected, so don't require it here
      // Only validate account if it's already selected (for currency/rate validation)
      if (formData.accountId && formData.accountId.trim() !== "") {
        // Check exchange rate if currency differs from account currency
        const account = accounts.find(acc => acc.id === formData.accountId);
        if (account) {
          const accountCurrency = account.currency || defaultCurrency;
          const selectedCurrency = formData.currency || accountCurrency;
          if (selectedCurrency !== accountCurrency) {
            if (!formData.rate || formData.rate.trim() === "" || parseFloat(formData.rate) <= 0) {
              // Focus on rate field - find by label
              setTimeout(() => {
                const rateLabel = Array.from(document.querySelectorAll('label')).find(label => label.textContent === 'Exchange Rate');
                if (rateLabel) {
                  const rateField = rateLabel.closest('.MuiFormControl')?.querySelector('input') as HTMLInputElement;
                  if (rateField) {
                    rateField.focus();
                    rateField.scrollIntoView({ behavior: "smooth", block: "center" });
                  }
                }
              }, 100);
              return false;
            }
          }
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate and focus first empty required field
    if (!validateAndFocusFirstEmpty()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Sync newly added detail options to category group details before creating transaction
      if (transactionType !== "transfer" && formData.category && Object.keys(newlyAddedDetailOptions).length > 0) {
        try {
          // Find the selected category and its group
          const selectedCategory = [...expenseCategories, ...incomeCategories].find(
            cat => cat.name === formData.category
          );
          
          if (selectedCategory && selectedCategory.groupId) {
            const group = categoryGroups.find(g => g.id === selectedCategory.groupId);
            
            // Ensure group exists and has valid name and ID
            if (group && group.id && group.name && group.details) {
              // Update group details with newly added options
              const updatedDetails = group.details.map((detail: TransactionCategoryDetail) => {
                const newOptions = newlyAddedDetailOptions[detail.name];
                if (newOptions && newOptions.length > 0 && detail.type === "list") {
                  // Merge new options with existing listItems
                  const existingItems = detail.listItems || [];
                  const mergedItems = [...existingItems, ...newOptions];
                  // Remove duplicates
                  const uniqueItems = Array.from(new Set(mergedItems));
                  return {
                    ...detail,
                    listItems: uniqueItems,
                  };
                }
                return detail;
              });

              // Update the group via API (only update details, not name or description)
              await api.updateGroup(workspaceId, group.id, undefined, undefined, updatedDetails);
              
              // Refresh category groups to get updated details
              const [expenseGroups, incomeGroups] = await Promise.all([
                api.getGroups(workspaceId, "expense"),
                api.getGroups(workspaceId, "income"),
              ]);
              // Category groups are managed by useMobileDataFetching hook
            }
          }
        } catch (error) {
          console.error("Error syncing new detail options:", error);
          // Don't block transaction creation if sync fails
        }
      }

      if (transactionType === "transfer") {
        // Handle transfer submission via API
        const fromAccount = accounts.find(acc => acc.id === transferFormData.fromAccountId);
        const toAccount = accounts.find(acc => acc.id === transferFormData.toAccountId);

        if (!fromAccount || !toAccount || !transferFormData.amount || (transferFormData.amount && transferFormData.amount.trim() === "")) {
          alert("Please fill in all required fields");
          return;
        }
        
        // Double-check amount is valid (allow 0)
        const transferAmount = parseFloat(transferFormData.amount);
        if (isNaN(transferAmount) || transferAmount < 0) {
          alert("Please enter a valid amount");
          return;
        }

        // Upload custom field files and merge into customFieldValues, then derive note/attachment
        let mergedTransferCustom: Record<string, string | { url: string; name: string; type: string }> = { ...(transferFormData.customFieldValues || {}) };
        if (transferFormData.customFieldPendingFiles && Object.keys(transferFormData.customFieldPendingFiles).length > 0) {
          setUploadingAttachment(true);
          for (const [fieldId, file] of Object.entries(transferFormData.customFieldPendingFiles)) {
            const path = `workspaces/${workspaceId}/attachments/${fieldId}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
            const fileRef = ref(storage, path);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            const contentType = file.type || "application/octet-stream";
            mergedTransferCustom[fieldId] = { url, name: file.name, type: contentType };
          }
          setUploadingAttachment(false);
        }
        const transferDerived = deriveNoteAndAttachmentFromCustomFields(transactionCustomFields, "transfer", mergedTransferCustom);

        // Prepare fee data
        const feeAmount = transferFormData.feeAmount && transferFormData.feeAmount.trim() !== "" 
          ? parseFloat(transferFormData.feeAmount) 
          : undefined;
        const feeAccountId = transferFormData.feeAccountId && transferFormData.feeAccountId.trim() !== ""
          ? transferFormData.feeAccountId
          : undefined;

        // Validate fee if provided (only when transfer fee is enabled)
        if (props.allowTransferFee && feeAmount !== undefined && feeAccountId) {
          if (isNaN(feeAmount) || feeAmount < 0) {
            alert("Please enter a valid fee amount");
            return;
          }
          const feeAccount = accounts.find(acc => acc.id === feeAccountId);
          if (!feeAccount) {
            alert("Fee account not found");
            return;
          }
        }

        const response = await api.makeAuthenticatedRequest(
          isEditMode && transactionId
            ? `/workspaces/${workspaceId}/transactions/${transactionId}`
            : `/workspaces/${workspaceId}/transactions/transfer`,
          {
            method: isEditMode && transactionId ? "PUT" : "POST",
            body: JSON.stringify({
              fromAccountId: transferFormData.fromAccountId,
              toAccountId: transferFormData.toAccountId,
              amount: parseFloat(transferFormData.amount),
              date: combineDateAndTime(transferFormData.date, transferFormData.time),
              note: transferDerived.note || undefined,
              transferRate: transferFormData.transferRate ? parseFloat(transferFormData.transferRate) : undefined,
              ...(props.allowTransferFee && feeAmount !== undefined && feeAccountId
                ? { feeAmount, feeAccountId }
                : {}),
              attachmentUrl: transferDerived.attachmentUrl || undefined,
              attachmentName: transferDerived.attachmentName || undefined,
              attachmentType: transferDerived.attachmentType || undefined,
              customFieldValues: Object.keys(mergedTransferCustom).length > 0 ? mergedTransferCustom : undefined,
            }),
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || `Failed to ${isEditMode ? "update" : "create"} transfer`);
        }
      } else {
        // Handle expense/income submission via API
        if (!formData.category || !formData.amount || (formData.amount && formData.amount.trim() === "")) {
          alert("Please fill in all required fields");
          return;
        }
        
        // Double-check amount is valid (allow 0)
        const amountValue = parseFloat(formData.amount);
        if (isNaN(amountValue) || amountValue < 0) {
          alert("Please enter a valid amount");
          return;
        }

        // Auto-select account if not selected when user clicks create
        let accountIdToUse = formData.accountId;
        if (!accountIdToUse || accountIdToUse.trim() === "") {
          // Try to find the most recent account used with this category
          const previousTransactionsWithCategory = transactions
            .filter(t => t.category === formData.category && t.accountId)
            .sort((a, b) => {
              const getDateValue = (date: any): number => {
                if (!date) return 0;
                if (date instanceof Date) return date.getTime();
                if (typeof date === 'string') return new Date(date).getTime();
                if (typeof date === 'number') return date;
                if (date.toDate && typeof date.toDate === 'function') return date.toDate().getTime();
                if (date.seconds) return date.seconds * 1000;
                return 0;
              };
              const dateA = getDateValue(a.date);
              const dateB = getDateValue(b.date);
              return dateB - dateA; // Most recent first
            });
          
          if (previousTransactionsWithCategory.length > 0) {
            // Use the most recent account used with this category
            accountIdToUse = previousTransactionsWithCategory[0].accountId || "";
          } else if (accounts.length > 0) {
            // Fallback to first available account
            accountIdToUse = accounts[0].id;
          }
          
          // Update formData with auto-selected account
          if (accountIdToUse) {
            const selectedAccount = accounts.find(acc => acc.id === accountIdToUse);
            if (selectedAccount) {
              const accountCurrency = selectedAccount.currency || defaultCurrency;
              setFormData(prev => ({
                ...prev,
                accountId: accountIdToUse,
                accountGroup: selectedAccount.group,
                // Set currency to account currency if not already set
                currency: prev.currency || accountCurrency,
              }));
            }
          }
        }

        if (!accountIdToUse || accountIdToUse.trim() === "") {
          alert("Please select an account");
          return;
        }

        const account = accounts.find(acc => acc.id === accountIdToUse);
        if (!account) {
          alert("Selected account not found");
          return;
        }

        // Upload custom field files and merge into customFieldValues, then derive note/attachment
        let mergedCustom: Record<string, string | { url: string; name: string; type: string }> = { ...(formData.customFieldValues || {}) };
        if (formData.customFieldPendingFiles && Object.keys(formData.customFieldPendingFiles).length > 0) {
          setUploadingAttachment(true);
          for (const [fieldId, file] of Object.entries(formData.customFieldPendingFiles)) {
            const path = `workspaces/${workspaceId}/attachments/${fieldId}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
            const fileRef = ref(storage, path);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            const contentType = file.type || "application/octet-stream";
            mergedCustom[fieldId] = { url, name: file.name, type: contentType };
          }
          setUploadingAttachment(false);
        }
        const scope = transactionType === "income" ? "income" : "expense";
        const derived = deriveNoteAndAttachmentFromCustomFields(transactionCustomFields, scope, mergedCustom);

        // Field stores account-to-default rate, but API needs expense-to-default rate
        // Convert: account-to-default -> expense-to-default
        // expenseAmount * expenseToDefaultRate = defaultAmount
        // accountAmount * accountToDefaultRate = defaultAmount
        // accountAmount = expenseAmount * exchangeRate
        // So: expenseAmount * expenseToDefaultRate = expenseAmount * exchangeRate * accountToDefaultRate
        // Therefore: expenseToDefaultRate = exchangeRate * accountToDefaultRate
        // Note: When expenseCurrency === accountCurrency, exchangeRate = 1, so expenseToDefaultRate = accountToDefaultRate
        const expenseCurrency = formData.currency || account.currency;
        const accountCurrency = account.currency;
        
        // Helper function to calculate transfer rate from currency settings
        const calculateTransferRate = (fromCurrency: string, toCurrency: string): number => {
          if (fromCurrency === toCurrency) return 1;
          const fromCurrencyObj = currencies.find((c) => c.code === fromCurrency);
          const toCurrencyObj = currencies.find((c) => c.code === toCurrency);
          const fromRate = fromCurrencyObj?.rate || (fromCurrency === defaultCurrency ? 1 : 1);
          const toRate = toCurrencyObj?.rate || (toCurrency === defaultCurrency ? 1 : 1);
          return toRate / fromRate;
        };
        
        // Calculate amount in account currency first
        const currenciesDiffer = expenseCurrency !== accountCurrency;
        let amountInAccountCurrency = parseFloat(formData.amount);
        let exchangeRate: number;
        
        if (currenciesDiffer) {
          if (!formData.rate || formData.rate.trim() === "") {
            alert("Please enter an exchange rate for currency conversion");
            setIsSubmitting(false);
            return;
          }
          exchangeRate = parseFloat(formData.rate);
          if (isNaN(exchangeRate) || exchangeRate <= 0) {
            alert("Please enter a valid exchange rate");
            setIsSubmitting(false);
            return;
          }
          // Convert expense amount to account currency
          amountInAccountCurrency = parseFloat(formData.amount) * exchangeRate;
        } else {
          // Same currency - rate is always 1
          exchangeRate = 1;
        }
        
        // Calculate amount in default currency
        // Field stores account-to-default rate, but we need expense-to-default rate for storage
        let expenseToDefaultRate: number | undefined = undefined;
        
        if (expenseCurrency !== defaultCurrency) {
          // If defaultCurrencyRate was manually edited, use it directly as expenseToDefaultRate
          // User wants to set the rate directly without any multiplication
          if (defaultCurrencyRateManuallyEdited && formData.defaultCurrencyRate && formData.defaultCurrencyRate.trim() !== "") {
            const parsedRate = parseFloat(formData.defaultCurrencyRate);
            if (!isNaN(parsedRate) && parsedRate > 0 && parsedRate >= 0.0001 && parsedRate <= 10000) {
              expenseToDefaultRate = parsedRate;
            }
            // If manually edited but value is invalid (e.g. accidental "0", "?", stale data), fall through to use calculated rate instead of blocking
          }
          if (expenseToDefaultRate === undefined) {
            // Auto-calculated or not manually edited - use the two-step conversion
            // Get account-to-default rate: use formData.defaultCurrencyRate if valid, otherwise calculate
            // Special case: if currencies match and rate was manually edited, use the rate as accountToDefaultRate
            let accountToDefaultRate: number;
            const currenciesMatch = expenseCurrency === accountCurrency;
            
            // If account currency equals default currency, accountToDefaultRate must be 1
            // Don't trust field value in this case - it might be stale/incorrect
            if (accountCurrency === defaultCurrency) {
              accountToDefaultRate = 1;
            } else if (formData.defaultCurrencyRate && formData.defaultCurrencyRate.trim() !== "") {
              const parsedRate = parseFloat(formData.defaultCurrencyRate);
              if (!isNaN(parsedRate) && parsedRate > 0 && parsedRate >= 0.0001 && parsedRate <= 10000) {
                // Only use field value if currencies match OR manually edited
                // When currencies differ and not manually edited, don't trust auto-filled values
                if (currenciesMatch || defaultCurrencyRateManuallyEdited) {
                  accountToDefaultRate = parsedRate;
                } else {
                  // Currencies differ and not manually edited - calculate from settings
                  accountToDefaultRate = calculateTransferRate(accountCurrency, defaultCurrency);
                }
              } else {
                // Invalid rate - check if currencies match and rate was manually set
                if (currenciesMatch && formData.rate) {
                  const manualRate = parseFloat(formData.rate);
                  if (!isNaN(manualRate) && manualRate > 0) {
                    // Use the manually edited rate as accountToDefaultRate
                    accountToDefaultRate = manualRate;
                  } else {
                    accountToDefaultRate = accountCurrency !== defaultCurrency 
                      ? calculateTransferRate(accountCurrency, defaultCurrency)
                      : 1;
                  }
                } else {
                  accountToDefaultRate = accountCurrency !== defaultCurrency 
                    ? calculateTransferRate(accountCurrency, defaultCurrency)
                    : 1;
                }
              }
            } else {
              // No defaultCurrencyRate set - check if currencies match and rate was manually set
              if (currenciesMatch && formData.rate) {
                const manualRate = parseFloat(formData.rate);
                if (!isNaN(manualRate) && manualRate > 0) {
                  // Use the manually edited rate as accountToDefaultRate
                  // This ensures expenseToDefaultRate = exchangeRate * accountToDefaultRate = 1 * rate = rate
                  accountToDefaultRate = manualRate;
                } else {
                  // No valid manual rate - use calculated rate from settings
                  accountToDefaultRate = accountCurrency !== defaultCurrency 
                    ? calculateTransferRate(accountCurrency, defaultCurrency)
                    : 1;
                }
              } else {
                // Currencies don't match or no rate set - require defaultCurrencyRate
                if (!currenciesMatch) {
                  alert("Please enter a default currency exchange rate for currency conversion");
                  setIsSubmitting(false);
                  return;
                }
                // Currencies match but no rate - use calculated rate from settings
                accountToDefaultRate = accountCurrency !== defaultCurrency 
                  ? calculateTransferRate(accountCurrency, defaultCurrency)
                  : 1;
              }
            }
            
            // Field stores account-to-default rate, convert to expense-to-default rate
            // expenseAmount * expenseToDefaultRate = defaultAmount
            // accountAmount * accountToDefaultRate = defaultAmount
            // accountAmount = expenseAmount * exchangeRate
            // So: expenseAmount * expenseToDefaultRate = expenseAmount * exchangeRate * accountToDefaultRate
            // Therefore: expenseToDefaultRate = exchangeRate * accountToDefaultRate
            // Note: When currencies match, rate = 1 (from earlier logic), so expenseToDefaultRate = 1 * accountToDefaultRate = accountToDefaultRate
            expenseToDefaultRate = exchangeRate * accountToDefaultRate;
          }
        } else if (expenseCurrency === defaultCurrency) {
          expenseToDefaultRate = 1;
        }

        // Amount in default currency: same formula as list preview (expense amount × expense-to-default rate)
        const amountInDefaultCurrency =
          expenseCurrency === defaultCurrency
            ? parseFloat(formData.amount)
            : (expenseToDefaultRate ?? 0) > 0
              ? parseFloat(formData.amount) * (expenseToDefaultRate as number)
              : amountInAccountCurrency;

        const requestBody = {
          type: transactionType,
          title: formData.category, // API requires title, use category name
          category: formData.category,
          amount: parseFloat(formData.amount),
          currency: expenseCurrency,
          rate: formData.rate ? parseFloat(formData.rate) : undefined,
          defaultCurrencyRate: expenseToDefaultRate,
          amountInDefaultCurrency,
          defaultCurrencyAtTime: defaultCurrency,
          date: combineDateAndTime(formData.date, formData.time),
          accountId: accountIdToUse,
          note: derived.note || undefined,
          details: formData.details,
          attachmentUrl: derived.attachmentUrl || undefined,
          attachmentName: derived.attachmentName || undefined,
          attachmentType: derived.attachmentType || undefined,
          customFieldValues: Object.keys(mergedCustom).length > 0 ? mergedCustom : undefined,
          ...(props.allowExpenseFee &&
            transactionType === "expense" &&
            formData.feeAmount != null &&
            formData.feeAccountId != null &&
            formData.feeAmount.trim() !== "" &&
            parseFloat(formData.feeAmount) > 0
            ? {
                feeAmount: parseFloat(formData.feeAmount),
                feeAccountId: formData.feeAccountId.trim(),
              }
            : {}),
        };
        
        const response = await api.makeAuthenticatedRequest(
          isEditMode && transactionId
            ? `/workspaces/${workspaceId}/transactions/${transactionId}`
            : `/workspaces/${workspaceId}/transactions`,
          {
            method: isEditMode && transactionId ? "PUT" : "POST",
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          let errorData: { error?: string; message?: string };
          try {
            errorData = await response.json();
          } catch {
            errorData = { error: response.statusText || `Server returned ${response.status}` };
          }
          const message = errorData.message || errorData.error || `Failed to ${isEditMode ? "update" : "create"} transaction`;
          throw new Error(message);
        }
      }

      // Clear newly added options after successful transaction creation
      setNewlyAddedDetailOptions({});
      
      // Clear saved state after successful transaction creation
      clearSavedState();
      
      // Clear route info so Activity doesn't auto-navigate back
      try {
        sessionStorage.removeItem(routeStorageKey);
      } catch (error) {
        console.error("Error clearing route info:", error);
      }

      // Mark that we're intentionally navigating back after successful creation
      isNavigatingBackRef.current = true;

      // Invalidate cache so Activity list refetches and shows the new/updated transaction (fixes iPhone not showing created transaction)
      invalidateTransactionsCache(workspaceId);

      // Navigate back - use returnUrl if provided (e.g., from CategoryGroupDetail), otherwise go to Activity
      if (props.returnUrl && workspaceSlug) {
        navigate(props.returnUrl, { replace: true });
      } else {
        navigate(`/${workspaceSlug}/activity`, { replace: true });
      }
    } catch (error: any) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} transaction:`, error);
      alert(error.message || `Failed to ${isEditMode ? "update" : "create"} transaction. Please try again.`);
    } finally {
      setIsSubmitting(false);
      setUploadingAttachment(false);
    }
  };

  return {
    handleSubmit,
    validateAndFocusFirstEmpty,
  };
};
